import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {
  GetParams,
  GetResponse,
  InvoiceOrder,
  InvoiceOrderCreate,
  Partner,
  PostResponse,
  PurchaseOrder
} from '@app/shared';
import {Observable} from 'rxjs';
import {convertGetParamsToHttpParams} from '../utils/api-utils';
import {GlobalValuesService} from './global-values.service';

@Injectable({
  providedIn: 'root'
})
export class InvoiceOrderService {
  /** URLs principales relacionadas con InvoiceOrder **/
  InvoiceOrderUrl = environment.financeApiBaseUrl + '/api/invoiceOrder';
  PurchaseOrderUrl = environment.financeApiBaseUrl + '/api/purchaseOrder';
  InvoiceOrderUrlPdf = environment.financeApiBaseUrl + '/api/invoiceOrder/file';
  PurchaseOrderPdfUrl = environment.financeApiBaseUrl + '/api/purchaseOrder/file';
  constructor(private http: HttpClient, private globalValuesService: GlobalValuesService) { }

  /** Metodos de obtener/crear/actualizar/eliminar InvoiceOrder **/
  getInvoiceOrder(getParams: GetParams, accessToken?: string): Observable<GetResponse> {
    let headers = new HttpHeaders();

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Realizamos la petición GET con los parámetros y, si es necesario, con el token de autorización
    return this.http.get<GetResponse>(this.InvoiceOrderUrl, {
      params: convertGetParamsToHttpParams(getParams),
      headers: headers  // Pasamos los headers
    });
  }


  createInvoiceOrder(newInvoiceOrder: InvoiceOrderCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.InvoiceOrderUrl, newInvoiceOrder);
  }

  updateInvoiceOrder(InvoiceOrder: InvoiceOrder): Observable<any> {
    return this.http.put(this.InvoiceOrderUrl, InvoiceOrder);
  }

  patchInvoiceOrder(value: any): Observable<any> {
    return this.http.patch(this.InvoiceOrderUrl, value);
  }

  patchFactureInvoiceOrder(payload: {
    id: number,
    clientInvoiceId: number | null,
    clientInvoiceDate: string | null,
    clientInvoiceAmount: number | null
  }): Observable<any> {
    return this.http.patch(this.InvoiceOrderUrl + '/client-invoice', payload);
  }

  deleteInvoiceOrder(invoiceOrderIds: number[]): Observable<any> {
    // Crear parámetros de consulta con múltiples valores para 'ids'
    let params = new HttpParams();
    invoiceOrderIds.forEach(id => {
      params = params.append('ids', id.toString());
    });

    return this.http.delete(this.InvoiceOrderUrl, { params });
  }

  /** Metodos de obtener/crear/actualizar/eliminar PurchaseOrder **/
  getPurchaseOrder(getParams: GetParams, accessToken?: string): Observable<GetResponse> {
    let headers = new HttpHeaders();

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Realizamos la petición GET con los parámetros y, si es necesario, con el token de autorización
    return this.http.get<GetResponse>(this.PurchaseOrderUrl, {
      params: convertGetParamsToHttpParams(getParams),
      headers: headers  // Pasamos los headers
    });
  }


  createPurchaseOrder(newPurchaseOrder: InvoiceOrderCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.PurchaseOrderUrl, newPurchaseOrder);
  }

  updatePurchaseOrder(PurchaseOrder: PurchaseOrder): Observable<any> {
    return this.http.put(this.PurchaseOrderUrl, PurchaseOrder);
  }

  patchPurchaseOrder(value: any): Observable<any> {
    return this.http.patch(this.PurchaseOrderUrl, value);
  }

  patchFacturePurchaseOrder(payload: {
    id: number,
    supplierInvoiceId: number | null,
    supplierInvoiceDate: string | null,
    supplierInvoiceAmount: number | null
  }): Observable<any> {
    return this.http.patch(this.PurchaseOrderUrl + '/supplier-invoice', payload);
  }

  deletePurchaseOrder(invoiceOrderIds: number[]): Observable<any> {
    // Crear parámetros de consulta con múltiples valores para 'ids'
    let params = new HttpParams();
    invoiceOrderIds.forEach(id => {
      params = params.append('ids', id.toString());
    });

    return this.http.delete(this.PurchaseOrderUrl, { params });
  }

  downloadInvoiceOrderPdf(orderId: number, partnerId: number, currencyId: number): Observable<Blob> {
    const params = new HttpParams()
      .set('invoiceOrderId', orderId.toString())
      .set('currencyId', currencyId.toString())
      .set('receptorId', partnerId.toString());
    return this.http.get(this.InvoiceOrderUrlPdf, {params, responseType: 'blob'});
  }

  downloadPurchaseorderPdf(orderId: number, partnerId: number, currencyId: number): Observable<Blob> {
    const params = new HttpParams()
      .set('purchaseOrderId', orderId.toString())
      .set('currencyId', currencyId.toString())
      .set('receptorId', partnerId.toString());
    return this.http.get(this.PurchaseOrderPdfUrl, {params, responseType: 'blob'});
  }


  generatePdf(isInvoiceOrder: boolean, orderId: number, currencyId: number, executorPartnerId: number): Observable<any> {
    const payload: any = {
      currencyId,
      executorPartnerId
    };

    if (isInvoiceOrder) {
      payload.invoiceOrderId = orderId;
    } else {
      payload.purchaseOrderId = orderId;
    }

    return this.http.post((isInvoiceOrder ? this.InvoiceOrderUrl : this.PurchaseOrderUrl) + '/regenerate-pdf', payload);
  }

  setFirmRouteData(order: InvoiceOrder | PurchaseOrder) {
    // Primero obtenemos todos los partners potenciales
    const revision = this.getPartner(order.revisionId || 0);
    const shipper = this.getPartner(order.shipperId || 0);
    const transmitter = this.getPartner(order.transmitterId || 0);
    const approver = this.getPartner(order.approverId || 0);
    const rejector = this.getPartner(order.rejectorId || 0);

    // Luego asignamos según el estado
    switch (order.state) {
      case 'Draft':
        order.transmitter = undefined;
        order.transmitterDate = undefined;
        order.revision = undefined;
        order.revisionDate = undefined;
        order.shipper = undefined;
        order.shipperDate = undefined;
        order.approver = undefined;
        order.approverDate = undefined;
        order.rejector = undefined;
        order.rejectedDate = undefined;
        break;

      case 'Issued':
        order.transmitter = transmitter;
        order.revision = undefined;
        order.revisionDate = undefined;
        order.shipper = undefined;
        order.shipperDate = undefined;
        order.approver = undefined;
        order.approverDate = undefined;
        order.rejector = undefined;
        order.rejectedDate = undefined;
        break;

      case 'Reviewed':
      case 'Rejected':
        order.transmitter = transmitter;
        order.revision = revision;
        order.rejector = rejector;
        order.shipper = undefined;
        order.shipperDate = undefined;
        order.approver = undefined;
        order.approverDate = undefined;

        break;

      case 'Sent':
        order.transmitter = transmitter;
        order.revision = revision;
        order.shipper = shipper;
        order.rejector = rejector;
        order.approver = undefined;
        order.approverDate = undefined;
        break;

      case 'Approved':
        // En estado approved mostramos todos
        order.transmitter = transmitter;
        order.revision = revision;
        order.shipper = shipper;
        order.approver = approver;
        order.rejector = rejector;
        break;

      default:
        // Para cualquier otro estado, no asignamos ninguno
        order.transmitter = undefined;
        order.revision = undefined;
        order.shipper = undefined;
        order.approver = undefined;
        order.rejector = undefined;
        order.revisionDate = undefined;
        order.shipperDate = undefined;
        order.approverDate = undefined;
        order.rejectedDate = undefined;

    }
  }

  getPartner(id: number): Partner | undefined {
    return this.globalValuesService.partnerlist.find(p => p.id === id);
  }
}
